import { EnhancedModel, Model } from '../__generated/configurator-builder.js'
import { implementCustomExpressions } from '../__generated/implementation.js'
import { BaseTypes, SeatingTypes } from '../__generated/model/index.js'
import { PLATFORM_BASE_ALTERNATIVE_MAP, SINGLE_BASE_ALTERNATIVE_MAP } from '../data/base-data.js'
import {
  FINISH_MAP,
  TUMBLED_EDGE_NORMAL_MAP_URL,
  TUMBLED_EDGE_NORMAL_SCALE,
  TUMBLED_EDGE_REPEAT_RATIO,
  WAVY_EDGE_UV_SCALE,
  WOOD_SPECIES_TEXTURE_URLS,
  getMetalMaterialProps,
  getWoodMaterialProps,
} from '../data/texture-data.js'

const REVERSE_SINGLE_BASE_MAP = Object.entries(SINGLE_BASE_ALTERNATIVE_MAP).reduce<Record<string, string>>(
  (acc, [doubleType, singleType]) => {
    if (!(singleType in acc)) acc[singleType] = doubleType
    return acc
  },
  {}
)

function resolveDoubleLegType(tableBase: EnhancedModel.$Base): string {
  if (EnhancedModel.$BaseDoubleLeg.is(tableBase)) return tableBase.type
  const doubleType = REVERSE_SINGLE_BASE_MAP[tableBase.type]
  if (doubleType) return doubleType
  const platformDoubleType = PLATFORM_BASE_ALTERNATIVE_MAP[tableBase.type]
  if (platformDoubleType) return platformDoubleType
  switch (tableBase.type) {
    case Model.AnglePictureFrameCTCBase.type:
      return Model.AnglePictureFrameBase.type
    case Model.ReflectionBase.type:
    case Model.ReflectionCTCBase.type:
      return Model.ReflectionBase.type
  }
  if (EnhancedModel.$BaseMetal.is(tableBase)) {
    return Model.XMetalBase.type
  }
  return 'BrewerBase'
}

function getBaseData(s: EnhancedModel.$BenchBasic) {
  const baseType = resolveDoubleLegType(s.$table.$base)
  return s.$table.$base.$baseDataMap[baseType as EnhancedModel.$Base['type']]
}

function getCenterBaseData(s: EnhancedModel.$BenchBasic) {
  const baseType = resolveDoubleLegType(s.$table.$base)
  if (baseType === Model.CaseyBase.type) {
    return s.$table.$base.$baseDataMap[Model.OswaldBase.type as EnhancedModel.$Base['type']]
  }
  if (baseType === Model.TriflectionBase.type) {
    return s.$table.$base.$baseDataMap[Model.AnglePictureFrameBase.type as EnhancedModel.$Base['type']]
  }
  return getBaseData(s)
}

function getFourLegWidth(s: EnhancedModel.$BenchBasic): number {
  const w = s.$table.$base.$baseDataMap[s.$table.$base.type as EnhancedModel.$Base['type']].sizing.width
  return typeof w === 'number' ? w : 0
}

function getFourLegInset(s: EnhancedModel.$BenchBasic): number {
  const data = s.$table.$base.$baseDataMap[s.$table.$base.type as EnhancedModel.$Base['type']] as Record<string, any>
  return data['positioning']?.legInset ?? 0
}

export const benchExpressions = implementCustomExpressions({
  BenchBasic: {
    $fileUrl: s => s.$table.$glbFileUrl,

    $benchTopObjectName: s => {
      switch (s.$table.$top.edgeStyle) {
        case Model.EdgeStyles.Dupont:
          return 'TOP_RECTANGLE_OGEE'
        case Model.EdgeStyles.NaturalWavy:
          return 'TOP_RECTANGLE_WAVY_EDGE'
        default:
          return 'TOP_RECTANGLE'
      }
    },

    $benchEdgeNormalMapUrl: s =>
      s.$table.$top.edgeStyle === Model.EdgeStyles.Tumbled
        ? TUMBLED_EDGE_NORMAL_MAP_URL
        : WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies].normal,

    $benchEdgeNormalScale: s =>
      s.$table.$top.edgeStyle === Model.EdgeStyles.Tumbled
        ? TUMBLED_EDGE_NORMAL_SCALE * 1.5
        : getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).normalScale,

    $benchEdgeRepeatX: s => {
      const isTumbled = s.$table.$top.edgeStyle === Model.EdgeStyles.Tumbled
      const isWavy = s.$table.$top.edgeStyle === Model.EdgeStyles.NaturalWavy
      const benchTumbledRepeatRatio = TUMBLED_EDGE_REPEAT_RATIO * 0.6
      const woodRatio = s.$table.$woodTextureSize * (isWavy ? WAVY_EDGE_UV_SCALE : 1)
      return isTumbled ? s.$benchTopWidth / benchTumbledRepeatRatio : s.$benchTopWidth / woodRatio
    },
    $benchEdgeRepeatY: s => {
      const isTumbled = s.$table.$top.edgeStyle === Model.EdgeStyles.Tumbled
      const isWavy = s.$table.$top.edgeStyle === Model.EdgeStyles.NaturalWavy
      const benchTumbledRepeatRatio = TUMBLED_EDGE_REPEAT_RATIO * 0.6
      const woodRatio = s.$table.$woodTextureSize * (isWavy ? WAVY_EDGE_UV_SCALE : 1)
      return isTumbled ? s.$benchLength / benchTumbledRepeatRatio : s.$benchLength / woodRatio
    },

    $benchBevelThickness: s =>
      s.$table.$top.edgeStyle === Model.EdgeStyles.Beveled
        ? s.$benchTopThickness - s.$table.$top.$straightPartOfBevelTopThickness
        : 0,

    $benchLegScale: s => {
      const base = getBaseData(s)
      const w = base.sizing.width
      return s.$benchBaseFootprint / (typeof w === 'number' ? w : w.scaleZero)
    },

    $benchBaseHeight: s => s.$benchHeight - s.$benchTopThickness,

    $benchNearVisible: s => s.$table.seatingType === SeatingTypes.Benches && s.$table.seatingAmount >= 1,
    $benchFarVisible: s => s.$table.seatingType === SeatingTypes.Benches && s.$table.seatingAmount >= 2,

    $benchNearApronVisible: s =>
      s.$benchNearVisible &&
      EnhancedModel.$ApronBasic.is(s.$table.$top.$apron) &&
      !EnhancedModel.$IvyBase.is(s.$table.$base),
    $benchFarApronVisible: s =>
      s.$benchFarVisible &&
      EnhancedModel.$ApronBasic.is(s.$table.$top.$apron) &&
      !EnhancedModel.$IvyBase.is(s.$table.$base),

    $benchLength: s => s.$table.$top.$length - s.$benchInsetFromTable * 2,
    $benchApronWidth: s => s.$benchTopWidth - s.$benchApronInset * 2,
    $benchApronLength: s => s.$benchLength - s.$benchApronInset * 2,

    $benchTopWidthValue: s => s.$benchTopWidth,
    $benchTopThicknessValue: s =>
      s.$table.$top.edgeStyle === Model.EdgeStyles.Beveled
        ? s.$table.$top.$straightPartOfBevelTopThickness
        : s.$benchTopThickness,
    $benchApronThicknessValue: s => s.$benchApronThickness,
    $benchBaseFootprintValue: s => s.$benchBaseWidthScaleZero - 0.1,

    $benchNearOffsetX: s => s.$table.$top.$width / 2 + s.$benchGap + s.$benchTopWidth / 2,
    $benchFarOffsetX: s => -(s.$table.$top.$width / 2 + s.$benchGap + s.$benchTopWidth / 2),

    $benchTopOffsetY: s => s.$benchHeight,
    $benchApronOffsetY: s => s.$benchHeight - s.$benchTopThickness,
    $benchLegScaleY: s => s.$benchBaseHeight / s.$table.$base.$defaultHeight,

    $benchCenterLegVisible: s => s.$table.$top.$length > s.$table.$top.$minTopLengthForExtraBase,

    $benchLegLeftOffsetZ: s => {
      const w = getBaseData(s).sizing.width
      const rogersInset =
        resolveDoubleLegType(s.$table.$base) === Model.RogersBase.type
          ? (typeof w === 'number' ? w : w.scaleZero) / 4
          : 0
      return -(s.$benchLength / 2 - s.$benchLegInsetDefault - rogersInset)
    },
    $benchLegRightOffsetZ: s => {
      const w = getBaseData(s).sizing.width
      const rogersInset =
        resolveDoubleLegType(s.$table.$base) === Model.RogersBase.type
          ? (typeof w === 'number' ? w : w.scaleZero) / 4
          : 0
      return s.$benchLength / 2 - s.$benchLegInsetDefault - rogersInset
    },

    $benchLegScaleX: s => s.$benchLegScale,
    $benchLegScaleZ: s => s.$benchLegScale,

    $benchBaseObjectName: s => getBaseData(s).objectName,
    $benchBaseWidthScaleZero: s => {
      const w = getBaseData(s).sizing.width
      return typeof w === 'number' ? w : w.scaleZero
    },
    $benchBaseWidthScaleOne: s => {
      const w = getBaseData(s).sizing.width
      return typeof w === 'number' ? w : w.scaleOne
    },

    $benchCenterBaseObjectName: s => getCenterBaseData(s).objectName,

    $benchCenterLegScale: s => {
      const w = getCenterBaseData(s).sizing.width
      return s.$benchBaseFootprint / (typeof w === 'number' ? w : w.scaleZero)
    },
    $benchCenterLegScaleX: s => s.$benchCenterLegScale,
    $benchCenterLegScaleZ: s => s.$benchCenterLegScale,

    $benchCenterBaseWidthScaleZero: s => {
      const w = getCenterBaseData(s).sizing.width
      return typeof w === 'number' ? w : w.scaleZero
    },
    $benchCenterBaseWidthScaleOne: s => {
      const w = getCenterBaseData(s).sizing.width
      return typeof w === 'number' ? w : w.scaleOne
    },
    $benchCenterBaseFootprintValue: s => s.$benchCenterBaseWidthScaleZero - 0.1,

    $topFinishColor: s => FINISH_MAP[s.$table.$top.finish]?.colorHex ?? '#FFFFFF',
    $textureUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.baseColor,
    $normalMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.normal,
    $roughnessMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.roughness,
    $aoMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.ambientOcclusion,
    // Wood material props (bench top and apron are always wood)
    $woodMaterialRoughness: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).roughness,
    $woodMaterialMetalness: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).metalness,
    $woodMaterialEnvMapIntensity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).envMapIntensity,
    $woodMaterialSpecularIntensity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).specularIntensity,
    $woodMaterialNormalScale: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).normalScale,
    $woodMaterialReflectivity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).reflectivity,

    $benchApronFinishColor: s =>
      s.$isFourLegSeparateBase || s.$isFourLegCombinedBase
        ? s.$table.$base.$finishColor ?? '#FFFFFF'
        : FINISH_MAP[s.$table.$top.finish]?.colorHex ?? '#FFFFFF',

    $baseFinishColor: s => s.$table.$base.$finishColor,
    $baseTextureUrl: s => s.$table.$base.$textureUrl,
    $baseNormalMapUrl: s => s.$table.$base.$normalMapUrl,
    $baseRoughnessMapUrl: s => s.$table.$base.$roughnessMapUrl,
    $baseAoMapUrl: s => s.$table.$base.$aoMapUrl,
    // Base material props (bench legs follow the table base - wood or metal)
    $baseMaterialRoughness: s => {
      if (getBaseData(s).baseType === BaseTypes.Metal) return getMetalMaterialProps().roughness
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).roughness
    },
    $baseMaterialMetalness: s => {
      if (getBaseData(s).baseType === BaseTypes.Metal) return getMetalMaterialProps().metalness
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).metalness
    },
    $baseMaterialEnvMapIntensity: s => {
      if (getBaseData(s).baseType === BaseTypes.Metal) return getMetalMaterialProps().envMapIntensity
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).envMapIntensity
    },
    $baseMaterialSpecularIntensity: s => {
      if (getBaseData(s).baseType === BaseTypes.Metal) return getMetalMaterialProps().specularIntensity
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).specularIntensity
    },
    $baseMaterialNormalScale: s => {
      if (getBaseData(s).baseType === BaseTypes.Metal) return getMetalMaterialProps().normalScale
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).normalScale
    },
    $baseMaterialReflectivity: s => {
      if (getBaseData(s).baseType === BaseTypes.Metal) return getMetalMaterialProps().reflectivity
      return getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).reflectivity
    },

    $benchTopRepeatX: s => {
      const isWavy = s.$table.$top.edgeStyle === Model.EdgeStyles.NaturalWavy
      return s.$benchTopWidth / (s.$table.$woodTextureSize * (isWavy ? WAVY_EDGE_UV_SCALE : 1))
    },
    $benchTopRepeatY: s => {
      const isWavy = s.$table.$top.edgeStyle === Model.EdgeStyles.NaturalWavy
      return s.$benchLength / (s.$table.$woodTextureSize * (isWavy ? WAVY_EDGE_UV_SCALE : 1))
    },
    $benchApronRepeatX: s => s.$benchApronLength / s.$table.$woodTextureSize,
    $benchApronRepeatY: s => s.$benchApronLength / s.$table.$woodTextureSize,
    $benchBaseRepeatX: s => {
      const ratio =
        getBaseData(s).baseType === BaseTypes.Metal ? s.$table.$base.$metalRepeatRatio : s.$table.$woodTextureSize
      return s.$benchBaseFootprint / ratio
    },
    $benchBaseRepeatY: s => {
      const ratio =
        getBaseData(s).baseType === BaseTypes.Metal ? s.$table.$base.$metalRepeatRatio : s.$table.$woodTextureSize
      return s.$benchHeight / ratio
    },

    $isFourLegSeparateBase: s => EnhancedModel.$BaseFourLegSeparate.is(s.$table.$base),
    $isFourLegCombinedBase: s => EnhancedModel.$BaseFourLegCombined.is(s.$table.$base),
    $isReflectionBase: s =>
      EnhancedModel.$ReflectionBase.is(s.$table.$base) || EnhancedModel.$ReflectionCTCBase.is(s.$table.$base),

    $benchNearDoubleLegVisible: s =>
      !!s.$benchNearVisible && !s.$isFourLegSeparateBase && !s.$isFourLegCombinedBase && !s.$isReflectionBase,
    $benchFarDoubleLegVisible: s =>
      !!s.$benchFarVisible && !s.$isFourLegSeparateBase && !s.$isFourLegCombinedBase && !s.$isReflectionBase,
    $benchNearCenterLegVisible: s =>
      !!s.$benchNearVisible &&
      !s.$isFourLegSeparateBase &&
      !s.$isFourLegCombinedBase &&
      !s.$isReflectionBase &&
      !!s.$benchCenterLegVisible,
    $benchFarCenterLegVisible: s =>
      !!s.$benchFarVisible &&
      !s.$isFourLegSeparateBase &&
      !s.$isFourLegCombinedBase &&
      !s.$isReflectionBase &&
      !!s.$benchCenterLegVisible,
    $benchNearFourLegVisible: s => !!s.$benchNearVisible && !!s.$isFourLegSeparateBase,
    $benchFarFourLegVisible: s => !!s.$benchFarVisible && !!s.$isFourLegSeparateBase,
    $benchNearFourLegCombinedVisible: s => !!s.$benchNearVisible && !!s.$isFourLegCombinedBase,
    $benchFarFourLegCombinedVisible: s => !!s.$benchFarVisible && !!s.$isFourLegCombinedBase,
    $benchNearReflectionVisible: s => !!s.$benchNearVisible && !!s.$isReflectionBase,
    $benchFarReflectionVisible: s => !!s.$benchFarVisible && !!s.$isReflectionBase,

    $benchFourLegObjectName: s =>
      s.$table.$base.$baseDataMap[s.$table.$base.type as EnhancedModel.$Base['type']].objectName,
    $benchFourLegScaleY: s => {
      const height = EnhancedModel.$TraditionalFlushBase.is(s.$table.$base)
        ? s.$benchBaseHeight + s.$benchTopThickness + 0.04
        : s.$benchBaseHeight
      return height / s.$table.$base.$defaultHeight
    },
    $benchFourLegScaleX: s => s.$benchFourLegScaleY,
    $benchFourLegScaleZ: s => s.$benchFourLegScaleY,

    $benchNearFourLegOuterOffsetX: s => {
      const scale = s.$benchFourLegScaleX!
      const edgeOffset = s.$benchTopWidth / 2 - (getFourLegWidth(s) * scale) / 2 - getFourLegInset(s) * scale
      return (s.$benchNearOffsetX ?? 0) + edgeOffset
    },
    $benchNearFourLegInnerOffsetX: s => {
      const scale = s.$benchFourLegScaleX!
      const edgeOffset = s.$benchTopWidth / 2 - (getFourLegWidth(s) * scale) / 2 - getFourLegInset(s) * scale
      return (s.$benchNearOffsetX ?? 0) - edgeOffset
    },
    $benchFarFourLegOuterOffsetX: s => {
      const scale = s.$benchFourLegScaleX!
      const edgeOffset = s.$benchTopWidth / 2 - (getFourLegWidth(s) * scale) / 2 - getFourLegInset(s) * scale
      return (s.$benchFarOffsetX ?? 0) - edgeOffset
    },
    $benchFarFourLegInnerOffsetX: s => {
      const scale = s.$benchFourLegScaleX!
      const edgeOffset = s.$benchTopWidth / 2 - (getFourLegWidth(s) * scale) / 2 - getFourLegInset(s) * scale
      return (s.$benchFarOffsetX ?? 0) + edgeOffset
    },

    $benchFourLegLeftOffsetZ: s => {
      const scale = s.$benchFourLegScaleZ!
      return -(s.$benchLength / 2 - (getFourLegWidth(s) * scale) / 2 - getFourLegInset(s) * scale)
    },
    $benchFourLegRightOffsetZ: s => {
      const scale = s.$benchFourLegScaleZ!
      return s.$benchLength / 2 - (getFourLegWidth(s) * scale) / 2 - getFourLegInset(s) * scale
    },

    $benchFourLegRepeatX: () => 0.5,
    $benchFourLegRepeatY: s => {
      const ratio =
        getBaseData(s).baseType === BaseTypes.Metal ? s.$table.$base.$metalRepeatRatio : s.$table.$woodTextureSize
      return s.$benchBaseHeight / ratio
    },

    $benchReflectionLengthScaleZero: s => {
      const sizing = getBaseData(s).sizing
      return 'length' in sizing && typeof sizing.length === 'object' ? sizing.length.scaleZero : 0
    },
    $benchReflectionLengthScaleOne: s => {
      const sizing = getBaseData(s).sizing
      return 'length' in sizing && typeof sizing.length === 'object' ? sizing.length.scaleOne : 0
    },

    $benchFourLegCombinedWidthScaleZero: s => {
      const w = s.$table.$base.$baseDataMap[s.$table.$base.type as EnhancedModel.$Base['type']].sizing.width
      return typeof w === 'object' ? w.scaleZero : 0
    },
    $benchFourLegCombinedWidthScaleOne: s => {
      const w = s.$table.$base.$baseDataMap[s.$table.$base.type as EnhancedModel.$Base['type']].sizing.width
      return typeof w === 'object' ? w.scaleOne : 0
    },
    $benchFourLegCombinedLengthScaleZero: s => {
      const sizing = s.$table.$base.$baseDataMap[s.$table.$base.type as EnhancedModel.$Base['type']].sizing
      return 'length' in sizing && typeof sizing.length === 'object' ? sizing.length.scaleZero : 0
    },
    $benchFourLegCombinedLengthScaleOne: s => {
      const sizing = s.$table.$base.$baseDataMap[s.$table.$base.type as EnhancedModel.$Base['type']].sizing
      return 'length' in sizing && typeof sizing.length === 'object' ? sizing.length.scaleOne : 0
    },

    // Crossbrace
    $benchCrossbraceType: s => getBaseData(s).crossbraceType ?? 'StandardCrossbrace',
    $benchNeedTwoCrossbraces: s => s.$table.$base.hasCrossbrace && !!s.$benchCenterLegVisible,

    $benchNearCrossbraceVisible: s => !!s.$benchNearDoubleLegVisible && s.$table.$base.hasCrossbrace,
    $benchFarCrossbraceVisible: s => !!s.$benchFarDoubleLegVisible && s.$table.$base.hasCrossbrace,
    $benchNearCrossbraceSecondaryVisible: s => !!s.$benchNearCrossbraceVisible && !!s.$benchNeedTwoCrossbraces,
    $benchFarCrossbraceSecondaryVisible: s => !!s.$benchFarCrossbraceVisible && !!s.$benchNeedTwoCrossbraces,

    $benchCrossbraceObjectName: s => s.$crossbraceDataMap[s.$benchCrossbraceType].objectName,

    $benchCrossbraceOffsetY: s => {
      const crossbraceData = getBaseData(s).crossbraceData
      return crossbraceData ? crossbraceData.offsetY * s.$benchLegScaleY : 0
    },
    $benchCrossbraceOffsetZ: s => (s.$benchNeedTwoCrossbraces ? s.$benchLegLeftOffsetZ! / 2 : 0),
    $benchCrossbraceSecondaryOffsetZ: s => (s.$benchNeedTwoCrossbraces ? s.$benchLegRightOffsetZ! / 2 : 0),

    $benchCrossbraceWidthShapekeyValue: s => {
      const crossbraceData = getBaseData(s).crossbraceData
      const heightRatio = s.$benchLegScaleY
      return crossbraceData ? crossbraceData.width * heightRatio : 0
    },
    $benchCrossbraceLengthShapekeyValue: s => {
      const heightRatio = s.$benchLegScaleY
      return (
        Math.abs(s.$benchLegLeftOffsetZ!) +
        (!s.$benchNeedTwoCrossbraces ? Math.abs(s.$benchLegRightOffsetZ!) : 0) +
        (getBaseData(s).crossbraceData?.lengthExtension ?? 0) * 2 * heightRatio
      )
    },
    $benchCrossbraceHeightShapekeyValue: s => {
      const crossbraceData = getBaseData(s).crossbraceData
      return crossbraceData ? crossbraceData.height * s.$benchLegScaleY : 0
    },
    $benchCrossbraceScaleShapekeyValue: s => s.$benchCrossbraceScaleScaleZero,

    $benchCrossbraceWidthScaleZero: s => s.$crossbraceDataMap[s.$benchCrossbraceType].sizing.width.scaleZero,
    $benchCrossbraceWidthScaleOne: s => s.$crossbraceDataMap[s.$benchCrossbraceType].sizing.width.scaleOne,
    $benchCrossbraceLengthScaleZero: s => s.$crossbraceDataMap[s.$benchCrossbraceType].sizing.length.scaleZero,
    $benchCrossbraceLengthScaleOne: s => s.$crossbraceDataMap[s.$benchCrossbraceType].sizing.length.scaleOne,
    $benchCrossbraceHeightScaleZero: s => s.$crossbraceDataMap[s.$benchCrossbraceType].sizing.height.scaleZero,
    $benchCrossbraceHeightScaleOne: s => s.$crossbraceDataMap[s.$benchCrossbraceType].sizing.height.scaleOne,
    $benchCrossbraceScaleScaleZero: s => s.$crossbraceDataMap[s.$benchCrossbraceType].sizing.scale.scaleZero,
    $benchCrossbraceScaleScaleOne: s => s.$crossbraceDataMap[s.$benchCrossbraceType].sizing.scale.scaleOne,

    $benchCrossbraceRepeatX: s => s.$benchCrossbraceHeightShapekeyValue / s.$table.$woodTextureSize,
    $benchCrossbraceRepeatY: s => s.$benchCrossbraceLengthShapekeyValue / s.$table.$woodTextureSize,

    $benchCrossbraceFinishColor: s => FINISH_MAP[s.$table.$base.finish]?.colorHex ?? '#FFFFFF',
    $benchCrossbraceTextureUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.baseColor,
    $benchCrossbraceNormalMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.normal,
    $benchCrossbraceRoughnessMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.roughness,
    $benchCrossbraceAoMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.ambientOcclusion,
    $benchCrossbraceRoughness: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).roughness,
    $benchCrossbraceMetalness: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).metalness,
    $benchCrossbraceEnvMapIntensity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).envMapIntensity,
    $benchCrossbraceSpecularIntensity: s =>
      getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).specularIntensity,
    $benchCrossbraceNormalScale: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).normalScale,
    $benchCrossbraceReflectivity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).reflectivity,

    $benchDimIndFixedY: s => s.$benchHeight,

    $benchReflectionLength: s => s.$benchLength - s.$benchLegInsetDefault * 3,

    $benchReflectionScaleX: s => (s.$benchBaseFootprint - s.$benchApronThicknessValue * 3) / s.$benchBaseWidthScaleZero,
    $benchReflectionScaleY: s => s.$benchLegScaleY,
    $benchReflectionScaleZ: s => s.$benchLegScaleY,
    // benchNearLengthDimInd
    $benchNearLengthDimIndFixedX: s => s.$benchNearOffsetX! + s.$benchTopWidth / 2,
    $benchNearLengthDimIndStartZ: s => s.$benchLength / 2,
    $benchNearLengthDimIndEndZ: s => -1 * (s.$benchLength / 2),

    // benchNearWidthDimInd
    $benchNearWidthDimIndStartX: s => s.$benchNearOffsetX! + s.$benchTopWidth / 2,
    $benchNearWidthDimIndFixedZ: s => -s.$benchLength / 2,
    $benchNearWidthDimIndEndX: s => s.$benchNearOffsetX! - s.$benchTopWidth / 2,

    // benchFarLengthDimInd
    $benchFarLengthDimIndFixedX: s => s.$benchFarOffsetX! - s.$benchTopWidth / 2,
    $benchFarLengthDimIndStartZ: s => s.$benchLength / 2,
    $benchFarLengthDimIndEndZ: s => -1 * (s.$benchLength / 2),

    // benchFarWidthDimInd
    $benchFarWidthDimIndStartX: s => s.$benchFarOffsetX! - s.$benchTopWidth / 2,
    $benchFarWidthDimIndFixedZ: s => -s.$benchLength / 2,
    $benchFarWidthDimIndEndX: s => s.$benchFarOffsetX! + s.$benchTopWidth / 2,
  },
})
